// Calculs de l'onglet Frais & débours : indicateurs, total par dossier, filtres.
import { expenseAge } from './expenses';

export interface ExpenseLike {
  id: string;
  dossier_id: string;
  nature: string;
  amount_ht: number | string;
  amount_ttc: number | string;
  piece_date: string;
  billed_at: string | null;
  billed_document_id: string | null;
  polar_disbursement_id: string | null;
  receipt_path: string | null;
}

/** Au-delà, l'avance est signalée comme ancienne. */
export const OLD_EXPENSE_DAYS = 60;

const c = (v: number | string) => Math.round(Number(v) * 100);

export interface ExpensesKpis {
  deboursAmount: number;
  deboursLines: number;
  deboursDossiers: number;
  fraisAmount: number;
  fraisLines: number;
  oldAmount: number;
  oldLines: number;
  billedMonthAmount: number;
  billedMonthPieces: number;
}

/** Débours à refacturer (TTC), frais à refacturer (HT), avances de plus de 60 jours (TTC), refacturé ce mois (TTC). */
export function expensesKpis(expenses: ExpenseLike[], now: Date): ExpensesKpis {
  let debours = 0;
  let deboursLines = 0;
  const deboursDossiers = new Set<string>();
  let frais = 0;
  let fraisLines = 0;
  let old = 0;
  let oldLines = 0;
  let billed = 0;
  const pieces = new Set<string>();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  for (const e of expenses) {
    if (e.billed_at) {
      const d = new Date(e.billed_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (key === monthKey) {
        billed += c(e.amount_ttc);
        pieces.add(e.billed_document_id ?? e.polar_disbursement_id ?? e.id);
      }
      continue;
    }
    if (e.nature === 'debours') {
      debours += c(e.amount_ttc);
      deboursLines += 1;
      deboursDossiers.add(e.dossier_id);
    } else {
      frais += c(e.amount_ht);
      fraisLines += 1;
    }
    if (expenseAge(e.piece_date, now) > OLD_EXPENSE_DAYS) {
      old += c(e.amount_ttc);
      oldLines += 1;
    }
  }
  return {
    deboursAmount: debours / 100,
    deboursLines,
    deboursDossiers: deboursDossiers.size,
    fraisAmount: frais / 100,
    fraisLines,
    oldAmount: old / 100,
    oldLines,
    billedMonthAmount: billed / 100,
    billedMonthPieces: pieces.size,
  };
}

/** Ce qui reste à refacturer (TTC) pour chaque dossier, du plus gros au plus petit. */
export function expensesByDossier(expenses: ExpenseLike[]): { dossier_id: string; amount: number; lines: number }[] {
  const map = new Map<string, { cents: number; lines: number }>();
  for (const e of expenses) {
    if (e.billed_at) continue;
    const row = map.get(e.dossier_id) ?? { cents: 0, lines: 0 };
    row.cents += c(e.amount_ttc);
    row.lines += 1;
    map.set(e.dossier_id, row);
  }
  return [...map.entries()]
    .map(([dossier_id, r]) => ({ dossier_id, amount: r.cents / 100, lines: r.lines }))
    .sort((a, b) => b.amount - a.amount);
}

export type ExpenseSegment = 'todo' | 'billed' | 'all';
export type ReceiptFilter = 'missing' | 'attached';

export interface ExpenseFilters {
  segment: ExpenseSegment;
  q: string;
  natures: string[];
  receipts: string[];
  dossiers: string[];
}

export function filterExpenses<T extends ExpenseLike & { haystack: string }>(rows: T[], f: ExpenseFilters): T[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.segment === 'todo' && r.billed_at) return false;
    if (f.segment === 'billed' && !r.billed_at) return false;
    if (f.natures.length > 0 && !f.natures.includes(r.nature)) return false;
    if (f.receipts.length > 0 && !f.receipts.includes(r.receipt_path ? 'attached' : 'missing')) return false;
    if (f.dossiers.length > 0 && !f.dossiers.includes(r.dossier_id)) return false;
    return !q || r.haystack.includes(q);
  });
}
